import React from 'react';
import { Languages } from 'lucide-react';
import { useLanguageContext } from '@/providers/LanguageProvider';

export function LanguageSelector() {
  const { language, setLanguage, supportedLanguages } = useLanguageContext();
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const currentLanguage = supportedLanguages.find((entry) => entry.code === language) || supportedLanguages[0];

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
        <button
          type="button"
          className="flex items-center gap-2 rounded-lg p-2 text-blue-700 hover:bg-blue-50/80 hover:text-blue-800 dark:text-blue-200 dark:hover:bg-blue-500/10"
          aria-label="Language selector"
          aria-expanded={open}
          onClick={() => setOpen((prev) => !prev)}
        >
          <Languages className="w-4 h-4" />
          <span className="hidden md:inline">
            {currentLanguage?.nativeName}
          </span>
          <span className="md:hidden">
            {language.toUpperCase()}
          </span>
        </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[200px] max-h-[min(80vh,20rem)] overflow-y-auto rounded-2xl border border-blue-100/80 bg-white/95 p-1.5 text-popover-foreground shadow-[0_20px_48px_-24px_rgba(15,23,42,0.45)] backdrop-blur-xl dark:border-slate-700/80 dark:bg-slate-900/90 z-[120]">
          {supportedLanguages.map((lang) => (
            <button
              type="button"
              key={lang.code}
              onClick={() => {
                setLanguage(lang.code);
                setOpen(false);
              }}
              className="flex w-full items-center justify-between rounded-xl px-2.5 py-2 text-left text-sm transition-colors hover:bg-blue-50 dark:hover:bg-blue-500/20"
            >
              <span>{lang.nativeName}</span>
              <span className="text-sm text-muted-foreground">{lang.code === language ? '✓' : lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}